import { Fragment, type ReactNode } from 'react';
import { Paragraph, Text } from '@hungryroot/ui-kit';
import { type SectionListData } from '../../components/SectionList/utils';
import privacyStyles from './Privacy.module.css';
import sectionStyles from '../../components/SectionList/SectionList.module.css';

// The SectionList wraps section titles as <h2>, so each subsection title
// renders as an <h3> with an id that supports deep linking
// (e.g. section-6-financial-incentive).
export const FINANCIAL_INCENTIVE_SECTION_ID = 'section-6-financial-incentive';

function SubsectionTitle({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return (
    <h5 id={id} className={privacyStyles['subsection-title']}>
      {children}
    </h5>
  );
}

// Ids and anchor links generated by getSectionId function in '../../components/SectionList/utils
export const privacySections: SectionListData = {
  information: {
    title: 'Information We May Collect',
    body: (
      <Fragment key="information">
        <SubsectionTitle id="section-1-1">
          1.1. Information Collected from You Directly
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may collect the following information from you directly:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>First and last name</Text>
          </li>
          <li>
            <Text>Billing and shipping postal address</Text>
          </li>
          <li>
            <Text>Telephone number</Text>
          </li>
          <li>
            <Text>Email address</Text>
          </li>
          <li>
            <Text>Demographic information</Text>
          </li>
          <li>
            <Text>
              Information regarding your interests and preferences, such as
              dietary preferences or restrictions
            </Text>
          </li>
          <li>
            <Text>
              Prescription medication use or health goals and results from using
              Hungryroot
            </Text>
          </li>
          <li>
            <Text>Information about your household size and demographics</Text>
          </li>
          <li>
            <Text>
              Your payment card number and related payment card information
              (card brand, expiration)
            </Text>
          </li>
          <li>
            <Text>Social media account handle</Text>
          </li>
          <li>
            <Text>Account username and password</Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may collect this information when you create an account, make a
          purchase, start a subscription, or otherwise interact with our
          Services; communicate with us, such as by connecting with customer
          service; or when you fill out a survey or participate in focus groups,
          video or audio conferences, or other research-related activities.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          If you submit personal information relating to other individuals (such
          as other members of your household) to us or our service providers in
          connection with your use of our Services, you represent that you have
          the authority to do so and that we may use such information in
          accordance with this Privacy Policy.
        </Paragraph>
        <SubsectionTitle id="section-1-2">
          1.2. Information Collected Automatically
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may collect some information automatically when you use the
          Services, through various tracking technologies, including cookies,
          clear GIFs, web beacons, software development kits (“SDKs”) and
          tracking pixels (together, “Cookies”). Cookies are small files or
          technologies placed on your computer or device when you visit a
          website, online service, or app. They help recognize visitors across
          one or more browsing sessions and across one or more websites. Cookies
          also help enable certain features (like remembering you so you do not
          have to re-log in), improve the Services, personalize your experience,
          and deliver advertising.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may use Cookies on our website, in emails we send to you, and in
          our mobile app. These Cookies collect information about your device,
          such as operating system name and version, device manufacturer and
          model, language, Internet browser type and version, device
          identifiers, and advertising IDs. They also collect information about
          your use of the Services, such as your IP address, the pages you
          visit, the links that you click on, how long you spend on the
          Services, and the websites from which you visit the Services.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may also collect information about your approximate location
          automatically when you use the Services. For example, your IP address
          tells us roughly where you are. We may also collect information about
          your precise location if you allow us to use your device’s location.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          You may be able to disable and manage some Cookies through your
          browser settings. Many web browsers will allow you to: (i) be notified
          when your device is about to receive a cookie and choose whether or
          not to accept it; (ii) disable existing cookies; or (iii)
          automatically reject all future cookies. If you use multiple browsers
          on the same device or if you use multiple devices, you will likely
          need to manage your settings for each browser or device individually.
        </Paragraph>
        <SubsectionTitle id="section-1-3">
          1.3. Information From Third-Party Sources
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may also collect information from third parties, including the
          following:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>
              Information from other users, including your contact information
              if they purchase a subscription for the household
            </Text>
          </li>
          <li>
            <Text>
              Information from publicly and commercially available sources, as
              permitted by law, including contact information, demographic and
              professional data, and interests
            </Text>
          </li>
          <li>
            <Text>
              Information from social media services if you interact with us
              through a social media service, such as your name, email address,
              friend or followers/following list, profile and demographic
              information, current city, comments about or reactions to our
              Services, and people/sites you follow
            </Text>
          </li>
          <li>
            <Text>
              Information from data analytics and advertising partners,
              including usage information, demographic information, information
              about your activity and transactions on other websites or
              platforms or with other companies, and inferred interests about
              you
            </Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom-large']}>
          <span className={privacyStyles.bold}>
            Other Information We Collect.
          </span>{' '}
          We may also collect other information about you, your device, or your
          use of the Services in ways that we describe to you at the point of
          collection or otherwise with your consent.
        </Paragraph>
      </Fragment>
    ),
  },
  processing: {
    title: 'How We May Process Your Information',
    body: (
      <Fragment key="processing">
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may process the above categories of information for the following
          business and commercial purposes:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>
              Deliver our services and carry out obligations relating to your
              periodic renewing subscription with us. For example, when you sign
              up to receive groceries through our Services, you enter into a
              subscription with us to make these grocery deliveries on your
              behalf. We must process your information to fulfill your orders.
            </Text>
          </li>
          <li>
            <Text>
              Provide, maintain, personalize, optimize, and improve the
              Services, including research and analytics regarding use of the
              Services, to provide a more personalized experience, or to
              remember you when you leave and return to the Services.
            </Text>
          </li>
          <li>
            <Text>
              Respond to your inquiries or requests for customer support.
            </Text>
          </li>
          <li>
            <Text>
              Effectuate billing and payment for your purchase of the Services.
            </Text>
          </li>
          <li>
            <Text>Effectuate sweepstakes and promotional offers.</Text>
          </li>
          <li>
            <Text>Conduct identification and authentication checks.</Text>
          </li>
          <li>
            <Text>Contact prospective customers.</Text>
          </li>
          <li>
            <Text>Conduct general research and aggregate reporting.</Text>
          </li>
          <li>
            <Text>
              For purposes where required by law (for example, to fulfill our
              regulatory obligations).
            </Text>
          </li>
          <li>
            <Text>
              Provide you with more relevant marketing and promotional content,
              including through online marketing and advertising, SMS, and
              direct mail.
            </Text>
          </li>
          <li>
            <Text>
              Detect, investigate, and prevent activities that may violate our
              policies or be fraudulent or illegal.
            </Text>
          </li>
          <li>
            <Text>
              Provide you with relevant information about our products and
              services, such as through our newsletter, including to help you
              learn about other Services that we offer, or other communications
              to which you may have subscribed.
            </Text>
          </li>
          <li>
            <Text>
              Protect the safety and/or integrity of our users, employees, the
              Services, and others.
            </Text>
          </li>
          <li>
            <Text>
              With your consent or for any other purpose described to you at the
              point of collection.
            </Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom']}>
          <span className={privacyStyles.bold}>Combined Information.</span> We
          may combine information we process through the Services with
          information we may receive from other sources, both online and
          offline. We may use such combined information in accordance with this
          Policy.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom-large']}>
          <span className={privacyStyles.bold}>Aggregate/Anonymous Data.</span>{' '}
          We may aggregate and/or anonymize any information we process through
          the Services so that such information can no longer be linked to you
          or your device. We may use such information for any purpose,
          including, without limitation, for research and marketing purposes,
          and we may disclose such information to third parties, including, but
          not limited to, advertisers, promotional partners, and sponsors.
        </Paragraph>
      </Fragment>
    ),
  },
  disclosure: {
    title: 'How We May Disclose Your Information',
    body: (
      <Fragment key="disclosure">
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may disclose your information to the following categories of
          recipients:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>
              Vendors and service providers that provide services such as
              website hosting, chat features, payment processing, order
              fulfillment &amp; shipping providers, information technology and
              related infrastructure provision, customer support, analytics
              services, email and direct mail delivery, auditing, and other
              services.
            </Text>
          </li>
          <li>
            <Text>
              Third parties, such as businesses whose products and services we
              think may interest you, businesses that provide part of the
              Services you are using or have requested, and co-branded program
              partners, or other third parties to which you authorize us to
              disclose your information. For example, we may engage in these
              disclosures when offering a co-branded promotion, contest, or
              sweepstakes.
            </Text>
          </li>
          <li>
            <Text>
              Any third parties, including any surviving entity or
              successors-in-interest, in the event of any reorganization,
              merger, sale, joint venture, assignment, transfer, or other
              disposition of all or any portion of our business, assets, or
              stock (including in connection with any bankruptcy or similar
              proceedings) and, in each case, any due diligence relating
              thereto.
            </Text>
          </li>
          <li>
            <Text>
              Business partners that display our ads on their digital
              properties, or to whose digital properties the Services link,
              including those partners that help us perform targeted
              advertising.
            </Text>
          </li>
          <li>
            <Text>
              Advertising partners, vendors, and advertising networks to select,
              deliver, and serve relevant advertising to you and others on our
              Services, through direct mail, and on third-party platforms.
            </Text>
          </li>
          <li>
            <Text>
              Analytics and search engine providers that assist us in the
              improvement and optimization of our site.
            </Text>
          </li>
          <li>
            <Text>
              As we believe necessary: (i) to comply with applicable law; (ii)
              investigate or take action regarding illegal activities, suspected
              fraud, or violations of Hungryroot&apos;s Terms of Use; (iii) to
              protect our rights, privacy, safety, or property, or the safety or
              property of yours or others; (iv) to allow us to pursue available
              remedies or limit our damages; and (v) to respond or comply with
              lawful requests from courts, law enforcement agencies, regulatory
              agencies, and other government authorities, which may include
              authorities outside your state or country of residence.
            </Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom-large']}>
          In some cases, when we disclose your information to third parties,
          those third parties will use and process your information in
          accordance with their own separate privacy policies.
        </Paragraph>
      </Fragment>
    ),
  },
  analyticsAdvertising: {
    title: 'Analytics and Targeted Advertising',
    body: (
      <Fragment key="analytics-advertising">
        <Paragraph className={sectionStyles['margin-bottom']}>
          Hungryroot uses Cookies to help us better understand how people use
          our Services and to personalize your experience with the Services,
          including the advertisements you may receive. We use Cookies for
          Analytics purposes to help us understand how visitors use our
          Services, track site performance, and to improve the Services. Google
          Analytics is one service we use for this purpose. Google provides an
          opt out of certain data collection for Google Analytics; for more
          information, see this link:{' '}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noreferrer"
            className={sectionStyles.link}
          >
            Google Analytics Opt-Out Browser Add-On
          </a>
          .
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We also use Cookies to help us deliver advertising tailored to your
          preferences or interests based on your activity on our Services and
          across the Internet. Cookies also help us track the number of times
          you’ve been shown an advertisement and help us measure the
          effectiveness of the advertisements you see. You can learn more about
          targeted advertising and consumer choice and privacy by visiting the{' '}
          <a
            href="https://optout.networkadvertising.org/?c=1"
            target="_blank"
            rel="noreferrer"
            className={sectionStyles.link}
          >
            Network Advertising Initiative
          </a>{' '}
          or the{' '}
          <a
            href="https://optout.aboutads.info/?c=2&lang=EN"
            target="_blank"
            rel="noreferrer"
            className={sectionStyles.link}
          >
            Digital Advertising Alliance
          </a>{' '}
          (including for mobile apps at{' '}
          <a
            href="https://youradchoices.com/appchoices"
            target="_blank"
            rel="noreferrer"
            className={sectionStyles.link}
          >
            https://youradchoices.com/appchoices
          </a>
          ). Please note you must separately opt out in each browser and on each
          device; if you clear cache or cookies, be sure to reset your
          preferences as applicable (such as in the browser context). We are not
          responsible to the extent that any such opt-outs are not honored by
          any advertising technology companies.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom-large']}>
          Even to the extent that you opt out of targeted advertising, you will
          still receive advertising, it just may not be as relevant to you.
        </Paragraph>
      </Fragment>
    ),
  },
  rights: {
    title: 'Rights Regarding Your Information',
    body: (
      <Fragment key="rights">
        <SubsectionTitle id="section-5-1">
          5.1. Marketing Communications
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          If you no longer wish to receive marketing-related emails, such as our
          newsletter, you may opt out by following the instructions contained in
          each such email, or by emailing your request to{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>
          . Some messages from Hungryroot are Services-related, such as
          confirmation of your order or correspondence with the Hungryroot
          support team. You may not opt out of such emails. You may also opt out
          of push notifications from the Hungryroot app by updating your device
          settings accordingly.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          If you wish to opt out of receiving direct mail marketing from us, you
          may do so by emailing your request to{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>
          .
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          With your consent, we may also send you SMS messages about your order
          or to provide other information to you. You may opt out of receiving
          SMS messages by replying “STOP” to the sender at any time. Please note
          that our partners, including our shipping partners, may also contact
          you directly using SMS regarding your order. These messages are
          separate from any SMS messages Hungryroot sends to you and you will
          need to manage your preferences regarding receiving messages from
          those partners separately.
        </Paragraph>
        <SubsectionTitle id="section-5-2">
          5.2. Additional State Privacy Rights
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          You may also opt out of receiving SMS messages by any other reasonable
          method, including by contacting us at{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>
          . Data obtained through our short code program will not be shared with
          any third parties for their marketing purposes.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Depending on your jurisdiction of residence (including Colorado,
          Connecticut, Delaware, Indiana, Iowa, Kentucky, Maryland, Minnesota,
          Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island,
          Tennessee, Texas, Utah, or Virginia), you may have the right to
          request the following actions with respect to your information:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>
              Access to a copy of certain information we hold about you;
            </Text>
          </li>
          <li>
            <Text>Delete your information, subject to certain exceptions;</Text>
          </li>
          <li>
            <Text>Correct information we have about you; and</Text>
          </li>
          <li>
            <Text>
              Opt out of processing your information for profiling in
              furtherance of decisions that produce legal or similarly
              significant effects, if relevant.
            </Text>
          </li>
          <li>
            <Text>
              Receive a copy of your personal information in a portable and, to
              the extent technically feasible, readily useable format (data
              portability).
            </Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Laws in certain states, such as Oregon and Minnesota, also allow you
          to request a list of the specific third parties, other than natural
          persons, to which we have disclosed your information. Where you have
          consented to us processing your sensitive data (as defined in
          applicable privacy laws), you have the right to revoke that consent.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          You can submit your request using{' '}
          <a
            href="https://airtable.com/shr3O0BHKqYS0uu5v"
            target="_blank"
            rel="noreferrer"
            className={sectionStyles.link}
          >
            this form
          </a>{' '}
          or email us at{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>
          . You can also correct certain information through your Hungryroot
          account.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may request additional information from you to verify your identity
          before responding to your request. You may also have the right to
          designate an authorized agent to make certain requests on your behalf.
          In order to verify your authorized agent, you must provide the
          authorized agent with signed, written permission to make such requests
          on your behalf or power of attorney. If permitted under applicable
          law, we may also follow up with you to verify your identity before
          processing the authorized agent’s request.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Depending on your jurisdiction, you may have the right to appeal our
          denial of your request. You can submit your appeal to{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>{' '}
          and we will respond as required by applicable law.
        </Paragraph>
        <SubsectionTitle id="section-5-3">
          5.3. Opting Out of Sales, Sharing, and Targeted Advertising
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          As described in Section 4 (“Analytics and Targeted Advertising”), we
          may use Cookies to serve you with targeted advertising on the Services
          and elsewhere on the Internet. We may also disclose certain
          identifiers, such as email address, commercial information, and
          demographic information with certain third-party platforms and vendors
          to serve you and audiences similar to you with advertising. The use of
          Cookies and your information for these purposes may be considered
          “sales” or use of personal advertising for “targeted advertising”
          under applicable state privacy laws for which you have a right to opt
          out.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          To opt out of our use of your information for these purposes, click
          the “Do Not Sell or Share My Personal Information” link in the footer
          of any Hungryroot website and follow the instructions. You must change
          your preferences on any device or browser you use to access the
          Services. If you use a cookie blocker, such as Ghostery, it may block
          your visibility of this tool or link, including in your web footer.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom-large']}>
          If we detect that you have established a legally recognized
          browser-based opt-out preference signal for your device or browser
          (such as Global Privacy Control), we will endeavor to recognize such
          preference in accordance with and to the extent required by applicable
          law.
        </Paragraph>
      </Fragment>
    ),
  },
  california: {
    title: 'Additional Information for California Residents',
    body: (
      <Fragment key="california">
        <Paragraph className={sectionStyles['margin-bottom']}>
          If you are a resident of California, the California Consumer Privacy
          Act (“CCPA”) require us to provide you with additional information
          about: (1) the categories of “personal information” (as defined in the
          CCPA) we process and (2) the categories of third parties to which we
          (a) disclose such personal information for a business purpose; (b)
          “share” personal information for “cross-context behavioral
          advertising”; and/or (c) “sell” such personal information.
        </Paragraph>
        <SubsectionTitle id="section-6-1">
          6.1. Personal Information We Collect and Disclose
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          The personal information we have collected within the last twelve (12)
          months about you, the purposes for such collection, as well as the
          categories of third parties with whom we have disclosed this
          information, are described in the table below.
        </Paragraph>
        <div className={privacyStyles['table-scroll']}>
          <table className={privacyStyles['privacy-table']}>
            <caption className={privacyStyles['visually-hidden']}>
              Personal Information We Collect and Disclose
            </caption>
            <thead>
              <tr>
                <th scope="col">Category of Personal Information Collected</th>
                <th scope="col">Processing Purposes</th>
                <th scope="col">Disclosure</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>Identifiers:</span> Such
                  as First and last name, alias, shipping and mailing address,
                  unique personal identifier, online identifier, Internet
                  Protocol (IP) address, email address, account name, or other
                  similar identifiers.
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience, or to remember you when you leave and return
                      to the Services.
                    </li>
                    <li>
                      Communicate with you, including by sending our newsletter
                      to you and providing you with relevant information about
                      our products and services, whether or not you are a
                      customer.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>
                      Effectuate billing and payment for your purchase of the
                      Services.
                    </li>
                    <li>Conduct identification and authentication checks.</li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Third-party partners</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>
                    Commercial Information:
                  </span>{' '}
                  Such as Products or services purchased from us, obtained, or
                  considered, or other purchasing or consuming histories or
                  tendencies.
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, or to provide a more personalized
                      experience.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>
                      Effectuate billing and payment for your purchase of the
                      Services.
                    </li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Third-party partners</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>
                    Characteristics of protected classifications under
                    California or federal law
                  </span>{' '}
                  (e.g., age, gender)
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>Conduct identification and authentication checks.</li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>Financial data:</span>{' '}
                  Such as Payment card number and information related to your
                  account, including card brand and expiration date.
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>
                      Effectuate billing and payment for your purchase of the
                      Services.
                    </li>
                    <li>Conduct identification and authentication checks.</li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>
                    Internet or other electronic network activity:
                  </span>{' '}
                  Such as IP address, device ID, and device settings (language
                  preference, time zone), login information, browser type and
                  version, browser plug-in types and versions, operating system
                  and platform, information about your visit on our website or
                  App, browsing history, search history, information collected
                  via cookie or pixel about a consumer&apos;s interaction with
                  our Site, App, or an advertisement.
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience, or to remember you when you leave and return
                      to the Services.
                    </li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Third-party partners</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>
                    Geolocation information:
                  </span>{' '}
                  Such as City, state and ZIP code associated with your IP
                  address.
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience, or to remember you when you leave and return
                      to the Services.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>
                      Effectuate billing and payment for your purchase of the
                      Services.
                    </li>
                    <li>Conduct identification and authentication checks.</li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Third-party partners</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>Inferences:</span> Such
                  as Profile reflecting a consumer’s dietary preferences and
                  propensities.
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience, or to remember you when you leave and return
                      to the Services.
                    </li>
                    <li>
                      Communicate with you, including by sending our newsletter
                      to you and providing you with relevant information about
                      our products and services, whether or not you are a
                      customer.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Third-party partners</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>
                    Sensitive personal information:
                  </span>{' '}
                  Such as Account log-in and password, information you provide
                  regarding specific dietary preferences, dietary restriction
                  requests, and health goals and results from using Hungryroot
                  (via, for example, surveys).
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience, or to remember you when you leave and return
                      to the Services.
                    </li>
                    <li>
                      Communicate with you, including by sending our newsletter
                      to you and providing you with relevant information about
                      our products and services, whether or not you are a
                      customer.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <span className={privacyStyles.bold}>
                    Other information that identifies or can be reasonably
                    associated with you
                  </span>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>
                      Deliver our Services and carry out obligations relating to
                      your periodic renewing subscription with us.
                    </li>
                    <li>
                      Provide, maintain, personalize, optimize, and improve the
                      Services, including research and analytics regarding use
                      of the Services, to provide a more personalized
                      experience, or to remember you when you leave and return
                      to the Services.
                    </li>
                    <li>
                      Communicate with you, including by sending our newsletter
                      to you and providing you with relevant information about
                      our products and services, whether or not you are a
                      customer.
                    </li>
                    <li>
                      Respond to your inquiries or requests for customer
                      support.
                    </li>
                    <li>
                      Effectuate billing and payment for your purchase of the
                      Services.
                    </li>
                    <li>Conduct identification and authentication checks.</li>
                    <li>Conduct general research and aggregate reporting.</li>
                    <li>To fulfill our legal and regulatory obligations.</li>
                    <li>
                      Provide you with more relevant content in marketing,
                      promotional, or other communications to which you may be
                      subscribed.
                    </li>
                    <li>
                      Detect, investigate, and prevent activities that may
                      violate our policies or be fraudulent or illegal.
                    </li>
                    <li>
                      In any other way we may describe when you provide the
                      information or with your consent.
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className={privacyStyles['table-list']}>
                    <li>Advertising and analytics providers</li>
                    <li>Third-party partners</li>
                    <li>Service providers</li>
                    <li>Entities as required by law and similar disclosures</li>
                    <li>
                      Entities in connection with a merger, sale, or other asset
                      transfers
                    </li>
                    <li>Other entities with your consent</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We obtain the categories of personal information listed above from the
          following categories of sources: (1) directly from you; (2) through
          your use of the Services; (3) marketing, affiliate, and analytics
          partners; and (4) social media platforms.
        </Paragraph>
        <SubsectionTitle id="section-6-2">
          6.2. Your California Privacy Rights
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Under the CCPA, California residents have the following rights
          regarding their personal information:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>
              Inform you about the categories of personal information we collect
              or disclose about you; the categories of sources of such personal
              information; the business or commercial purpose for collecting
              your personal information; and the categories of third parties
              with whom we share/disclose personal information.
            </Text>
          </li>
          <li>
            <Text>
              Provide access to and/or a copy of certain personal information we
              hold about you.
            </Text>
          </li>
          <li>
            <Text>
              Correct inaccurate personal information that we maintain about
              you.
            </Text>
          </li>
          <li>
            <Text>Delete certain personal information we have about you.</Text>
          </li>
          <li>
            <Text>
              Opt out of “automated decisionmaking technology” to make a
              “significant decision” about you (as those terms are defined in
              the CCPA and if applicable).
            </Text>
          </li>
          <li>
            <Text>
              Receive a copy of your personal information in a portable and, to
              the extent technically feasible, readily useable format.
            </Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom']}>
          You may have the right to receive information about the financial
          incentives that we offer to you (if any). You also have the right not
          to be discriminated against (as provided for in applicable law) for
          exercising certain of your rights. Certain information may be exempt
          from such requests under applicable law. For example, we need certain
          types of information so that we can provide the Services to you. If
          you ask us to delete it, you may no longer be able to access or use
          the Services.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          You can submit your request using{' '}
          <a
            href="https://airtable.com/shr3O0BHKqYS0uu5v"
            target="_blank"
            rel="noreferrer"
            className={sectionStyles.link}
          >
            this form
          </a>{' '}
          or email us at{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>
          . You can also correct certain information through your Hungryroot
          account.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We may request additional information from you to verify your identity
          before responding to your request. You may also have the right to
          designate an authorized agent to make certain requests on your behalf.
          In order to verify your authorized agent, you must provide the
          authorized agent with signed, written permission to make such requests
          on your behalf or power of attorney. If permitted under applicable
          law, we may also follow up with you to verify your identity before
          processing the authorized agent’s request.
        </Paragraph>
        <SubsectionTitle id="section-6-3">
          6.3. Sensitive Personal Information
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          The CCPA also allows you to limit the use or disclosure of your
          “sensitive personal information” (as defined in the CCPA) if your
          sensitive personal information is used for certain purposes. Please
          note that we do not use or disclose sensitive personal information
          other than for purposes for which you cannot opt out under the CCPA.
        </Paragraph>
        <SubsectionTitle id="section-6-4">
          6.4. “Selling” and “Sharing” Personal Information
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          The CCPA sets forth certain obligations for businesses that “sell” or
          “share” personal information, as those terms as uniquely defined in
          the CCPA.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          <span className={privacyStyles.bold}>
            “Sales” of personal information.
          </span>{' '}
          We do not “sell” personal information as the term “sell” is commonly
          understood. However, we may disclose certain identifiers (like IP
          address and email address), internet or other electronic network
          activity information (like information regarding an individual’s
          browsing interactions on the Services), and commercial information
          (like the fact that a browser visited a page directed to people who
          are considering using the Services) to certain analytics, advertising,
          and marketing providers in ways that may be considered “sales” under
          the CCPA. To opt out of such disclosures, click the “Your Privacy
          Choices” link in our website footer.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Categories of personal information sold: identifiers (such as IP
          address and email address), internet or other electronic network
          activity information, and commercial information. Categories of third
          parties to whom personal information is sold: analytics providers and
          advertising and marketing partners.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          <span className={privacyStyles.bold}>
            “Sharing” personal information.
          </span>{' '}
          Under the CCPA, “sharing” is defined as the targeting of advertising
          to a consumer based on that consumer’s personal information obtained
          from the consumer’s activity across websites. We “share” information
          for these purposes to provide more relevant and tailored advertising
          to you regarding the Services. As part of this advertising, we may
          “share” certain identifiers (like IP address and email address),
          internet or other electronic network activity information (like
          information regarding an individual’s browsing interactions on the
          Services), and commercial information (like the fact that a browser
          visited a page directed to people who are considering using the
          Services) with advertising platforms and advertising networks. To opt
          out of “sharing” of your personal information, click the “Your Privacy
          Choices” link in our website footer.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We do not knowingly “sell” or “share” the personal information of
          children under 16.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Categories of personal information shared: identifiers (such as IP
          address and email address), internet or other electronic network
          activity information, and commercial information. Categories of third
          parties with whom personal information is shared: advertising
          platforms and advertising networks.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          To opt out of our use of your information for these purposes, click
          the “Do Not Sell or Share My Personal Information” link in the footer
          of any Hungryroot website and follow the instructions. You must change
          your preferences on any device you use to access the Services. If you
          use a cookie blocker, such as Ghostery, it may block your visibility
          of this tool or link, including in your web footer.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          If we detect that you have established a legally recognized
          browser-based opt-out preference signal for your device or browser
          (such as Global Privacy Control), we will endeavor to recognize such
          preference in accordance with and to the extent required by applicable
          law.
        </Paragraph>
        <SubsectionTitle id="section-6-5">6.5. Retention</SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          We keep your information to fulfill the purposes described above,
          unless a longer retention period is required or permitted by law. Our
          retention is based upon the amount, nature, and sensitivity of the
          information, the potential risk of harm from unauthorized use or
          disclosure of the information, the purposes for which we process the
          information, applicable legal requirements, and our legitimate
          interests. The purposes for which we process information may dictate
          different retention periods for the same types of information. For
          example, we retain your email address as an authentication credential
          (where applicable) as long as you have an account with us, and an
          additional period of time after that for our legitimate interests and
          for our fraud and legal compliance purposes. If you opt out of email
          marketing, we maintain your email on our suppression list for an
          extended time to honor your opt out. We may delete or de-identify your
          information sooner if we receive a verifiable deletion request,
          subject to exemptions under applicable law. As a general guide, we
          retain account and transaction data for the duration of your
          subscription plus seven (7) years for tax and legal compliance
          purposes; marketing suppression lists are retained indefinitely to
          honor your opt-out preferences; and cookies and tracking data are
          retained for up to twenty-four (24) months from collection.
        </Paragraph>
        <SubsectionTitle id={FINANCIAL_INCENTIVE_SECTION_ID}>
          6.6. Notice of Financial Incentive
        </SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          From time to time, we may offer financial incentives to promote our
          Services. These financial incentives may include:
        </Paragraph>
        <ul className={sectionStyles['list-indented-bullet-privacy']}>
          <li>
            <Text>
              Discounts, coupons, and special offers when you sign up for our
              email list, participate in marketing promotions or sweepstakes,
              refer a friend, or create an account.
            </Text>
          </li>
          <li>
            <Text>
              Points programs, where you earn points for certain purchases that
              can be redeemed for discounts or coupons.
            </Text>
          </li>
        </ul>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Sometimes, we may ask you to provide personal information in
          connection with these promotions. For example, when you create an
          account in exchange for a free gift, we may ask for your first and
          last name, shopping address, and phone number.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Participation in any financial incentive program is optional. You may
          withdraw from the program at any time by emailing{' '}
          <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
            help@hungryroot.com
          </a>
          . To stop receiving Hungryroot coupons or discounts in your email, you
          may unsubscribe from our emails by clicking the “unsubscribe” button
          at the bottom of any such email.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          The value of any financial incentive we offer is reasonably related to
          the value of any personal information you provide to us. We estimate
          the value of your personal information by considering, without
          limitation, the expenses we incur from collecting your personal
          information and/or providing the financial incentive to you, the
          revenue generated by your use of the financial incentive, and any
          improvements we can make to our products and services based on
          aggregating information obtained through the financial incentive
          program. Based on this methodology, we estimate the value of the
          personal information collected in connection with our financial
          incentive programs to be equal to the value of the discount or other
          benefit offered to you.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          Please note that we may provide additional terms that apply to a
          particular financial incentive. If applicable, those terms will be
          presented to you at sign up.
        </Paragraph>
        <SubsectionTitle id="section-6-7">6.7. Shine the Light</SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom']}>
          California’s Shine the Light Law (CA Civil Code § 1798.83) (“Shine the
          Light”) allows California residents with whom a business has an
          established business relationship for non-commercial purposes
          (“customers”) to request a list of the “third parties” to which the
          business has disclosed certain “personal information” in the prior 12
          months where the business knows, or reasonably should know, that the
          third parties used the personal information for their own “direct
          marketing purposes” (each term as defined by Shine the Light). Direct
          marketing purposes generally means using the personal information to
          solicit or induce a purchase, rental, lease, or exchange of products,
          goods, property, or services directly to individuals via mail,
          telephone, or email for their non-commercial purposes.
        </Paragraph>
        <Paragraph className={sectionStyles['margin-bottom']}>
          A business is not required to provide a list of such third parties if
          the business adopts a policy of only disclosing “personal information”
          to “third parties” for their “direct marketing purposes” if the
          customer (1) affirmatively agrees to the disclosure or (2) has not
          opted out of such disclosure. Hungryroot maintains such a policy and
          is therefore not required to disclose such a list. As noted herein,
          Hungryroot permits customers to opt out of the “sharing” of their
          personal information for cross-context behavioral advertising and
          “sales” of their personal information, as such terms are defined under
          the CCPA. This opt out applies to any disclosures covered by Shine the
          Light if applicable. To exercise such rights, please click the “Your
          Privacy Choices” link in the footer of this website. That said, in the
          prior calendar year, Hungryroot did not engage in any disclosures for
          which it knows or reasonably should know that the third-party
          recipients use the personal information for their own direct marketing
          purposes as defined by Shine the Light.
        </Paragraph>
        <SubsectionTitle id="section-6-8">6.8. Do Not Track</SubsectionTitle>
        <Paragraph className={sectionStyles['margin-bottom-large']}>
          Some web browsers may transmit a “do-not-track” signal. Because there
          currently is no industry standard concerning how to treat such
          signals, the Platform currently does not take action in response to do
          not track signals. Please note that Do Not Track is a different
          privacy mechanism than the Global Privacy Control browser choice
          referenced above.
        </Paragraph>
      </Fragment>
    ),
  },
  colorado: {
    title: 'Additional Information for Colorado Residents',
    body: (
      <Paragraph className={sectionStyles['margin-bottom-large']}>
        For disclosures required by the Colorado Privacy Act regarding the
        categories of personal information, processing purposes, and
        disclosures, please see the Section 6.1 (“Personal Information We
        Collect and Disclose”) in the “Information for California Residents”
        section above.
      </Paragraph>
    ),
  },
  security: {
    title: 'Security',
    body: (
      <Paragraph className={sectionStyles['margin-bottom-large']}>
        We take steps to ensure that your information is treated securely and in
        accordance with this Privacy Policy. We also implement reasonable
        measures to help protect information about you from loss, theft, misuse
        and unauthorized access, disclosure, alteration, and destruction.
        Unfortunately, no method of transmission over the internet or method of
        electronic storage is 100% secure. You are responsible for keeping
        confidential any passwords you use to access our Services and your email
        account, and for keeping your SMS destination secure. We ask you not to
        share this password with anyone else and not to use this password for
        other services or products. To the fullest extent permitted by
        applicable law, we do not accept liability for any unauthorized
        disclosure.
      </Paragraph>
    ),
  },
  minors: {
    title: 'Persons Under 18 Years of Age',
    body: (
      <Paragraph className={sectionStyles['margin-bottom-large']}>
        We do not knowingly collect the personal information of children under
        eighteen (18) years of age. If we learn that we have collected such
        personal information, we will take reasonable steps to remove such
        information from our database.
      </Paragraph>
    ),
  },
  thirdPartySites: {
    title: 'Third-Party Sites',
    body: (
      <Paragraph className={sectionStyles['margin-bottom-large']}>
        Note that this Privacy Policy does not apply to the practices of third
        parties that Hungryroot does not own or control, or individuals that
        Hungryroot does not employ or manage; you are subject to the privacy
        policies of those third parties. Hungryroot is not responsible for the
        privacy or security practices of other websites on the Internet, even
        those linked to or from the Services. We encourage you to review the
        privacy policy of any entity whose products or services you use through
        the Services.
      </Paragraph>
    ),
  },
  updates: {
    title: 'Updates to this Privacy Policy',
    body: (
      <Paragraph className={sectionStyles['margin-bottom-large']}>
        Any changes we may make to this Privacy Policy will be posted on this
        page and, where required by law, you may be notified by email, in the
        App, or other places that Hungryroot deems appropriate. Please check
        back here frequently to see any updates or changes to this Privacy
        Policy. Your continued use of our Services following the posting of any
        changes will mean that you accept the changes we have made. The
        “Effective Date” at the top of this Privacy Policy indicates the date on
        which this Privacy Policy was last updated.
      </Paragraph>
    ),
  },
  contact: {
    title: 'Contact Us',
    body: (
      <Paragraph>
        If you have questions or suggestions you can contact Hungryroot&apos;s
        support team by sending an email to{' '}
        <a href="mailto:help@hungryroot.com" className={sectionStyles.link}>
          help@hungryroot.com
        </a>
        .
      </Paragraph>
    ),
  },
};
